import { CircleCloseIcon } from 'assets/icons';
import { CustomButton, CustomModal, CustomTextArea } from 'components';
import { FC, useEffect, useState } from 'react';
import { ButtonType } from 'components/custom-button/CustomButton';

export interface NoteData {
  id?: number | string;
  content: string;
}

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
  defaultNote?: string;
  isEdit?: boolean;
  title?: string;
  placeholder?: string;
  saveButtonText?: string;
  isLoading?: boolean;
}

const NoteModal: FC<NoteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultNote = '',
  isEdit = false,
  title = isEdit ? 'Edit note' : 'Create note',
  placeholder = 'Enter note here',
  saveButtonText = isEdit ? 'Save' : 'Add',
  isLoading = false
}) => {
  const [noteText, setNoteText] = useState(defaultNote);

  useEffect(() => {
    if (isOpen) {
      setNoteText(defaultNote);
    }
  }, [isOpen, defaultNote]);

  const handleClose = () => {
    setNoteText('');
    onClose();
  };

  const handleSave = async () => {
    if (!noteText.trim()) return;

    try {
      await onSave(noteText.trim());
      handleClose();
    } catch (error) {
      // Error handling is done by RTK Query
    }
  };

  const isSaveDisabled = () => {
    if (isEdit) {
      return noteText.trim() === defaultNote.trim();
    }
    return !noteText.trim();
  };

  return (
    <CustomModal open={isOpen} width="md:w-[610px]">
      <div className="relative flex grow flex-col">
        <CircleCloseIcon onClick={handleClose} className="absolute right-2 top-2 shrink-0" />

        <div className="flex items-center justify-between px-10 pt-9">
          <div className="text-xl font-bold text-headingGray">{title}</div>
        </div>
        <div className="px-10 pt-4 pb-2.5">
          <CustomTextArea
            id={isEdit ? 'edit-note-text-input' : 'create-note-text-input'}
            name="note-input"
            placeholder={placeholder}
            className="h-[162px] max-h-[162px] py-3 px-5 focus:outline-none"
            value={noteText}
            onChange={setNoteText}
            maxLength={1000}
            showRemainingChar={true}
            autoFocus={true}
          />
        </div>

        <div className="flex items-center justify-end py-4 pr-10">
          <CustomButton
            id="note-modal-cancel-button"
            type={ButtonType.SECONDARY}
            onClick={handleClose}
            className="mr-5 w-20">
            Cancel
          </CustomButton>
          <CustomButton
            id="note-modal-save-button"
            type={ButtonType.PRIMARY}
            onClick={handleSave}
            disabled={isSaveDisabled()}
            className="w-20"
            isLoading={isLoading}>
            {saveButtonText}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default NoteModal;
